import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import config from '../config/environment';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import ReactJsonView from '@microlink/react-json-view';
import { useApp } from '../context/AppContext';

import { apiService } from '../services/api';

const APIPlayground = () => {
  const { t } = useTranslation();
  const { websocketConnected } = useApp();

  const [selectedEndpoint, setSelectedEndpoint] = useState('');
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('/api/v1/metrics/system');
  const [headers, setHeaders] = useState('{}');
  const [body, setBody] = useState('{}');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [apiDocs, setApiDocs] = useState(null);

  const endpoints = [
    { path: '/health', method: 'GET', description: t('system_health_check') },
    { path: '/api/v1/system/stats', method: 'GET', description: t('system_statistics') },
    { path: '/api/v1/sandbox/status', method: 'GET', description: t('sandbox_status_desc') },
    { path: '/api/v1/sandbox/environments', method: 'GET', description: t('available_environments') },
    { path: '/api/v1/sandbox/execute', method: 'POST', description: t('execute_code_desc') },
    { path: '/api/v1/metrics/system', method: 'GET', description: t('system_metrics_desc') },
    { path: '/api/v1/metrics/performance', method: 'GET', description: t('performance_metrics_desc') },
    { path: '/api/v1/benchmark/load-test', method: 'POST', description: t('start_load_test_desc') },
    { path: '/api/v1/benchmark/benchmark-suites', method: 'GET', description: t('benchmark_suites_desc') },
  ];

  const sampleBodies = {
    '/api/v1/sandbox/execute': {
      code: 'print("Hello, DevSandbox Pro!")',
      language: 'python',
      timeout: 30,
      environment: 'default'
    },
    '/api/v1/benchmark/load-test': {
      target_url: config.BACKEND_BASE_URL,
      concurrent_users: 10,
      duration_seconds: 60,
      test_name: 'API Test'
    }
  };

  useEffect(() => {
    // Load API documentation from OpenAPI endpoint
    loadApiDocs();
  }, []);

  const loadApiDocs = async () => {
    try {
      // In a real implementation, this would fetch from /docs/openapi.json
      setApiDocs({
        info: {
          title: t('devsandbox_pro_api'),
          version: '1.0.0',
          description: t('api_description')
        }
      });
    } catch (err) {
      // Silent error handling
    }
  };

  const handleEndpointSelect = (endpoint) => {
    setSelectedEndpoint(endpoint.path);
    setMethod(endpoint.method);
    setUrl(endpoint.path); // Use relative URL to avoid CSP issues
    
    // Set sample body if available
    if (sampleBodies[endpoint.path]) {
      setBody(JSON.stringify(sampleBodies[endpoint.path], null, 2));
    } else {
      setBody('{}');
    }
  };

  const executeRequest = async () => {
    setLoading(true);
    setResponse(null);

    try {
      let parsedHeaders = {};
      let parsedBody = null;

      // Parse headers
      try {
        parsedHeaders = JSON.parse(headers);
      } catch (err) {
        throw new Error(t('invalid_json_headers'));
      }

      // Parse body for POST/PUT requests
      if (['POST', 'PUT', 'PATCH'].includes(method)) {
        try {
          parsedBody = JSON.parse(body);
        } catch (err) {
          throw new Error(t('invalid_json_request_body'));
        }
      }

      const startTime = Date.now();
      
      // Make the request
      let result;
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...parsedHeaders
        }
      };

      switch (method) {
        case 'GET':
          result = await fetch(url, { ...config, method: 'GET' });
          break;
        case 'POST':
          result = await fetch(url, {
            ...config,
            method: 'POST',
            body: JSON.stringify(parsedBody)
          });
          break;
        case 'PUT':
          result = await fetch(url, {
            ...config,
            method: 'PUT',
            body: JSON.stringify(parsedBody)
          });
          break;
        case 'DELETE':
          result = await fetch(url, { ...config, method: 'DELETE' });
          break;
        default:
          throw new Error('Unsupported HTTP method');
      }

      const responseTime = Date.now() - startTime;
      const responseData = await result.text();
      
      let parsedResponse;
      try {
        parsedResponse = JSON.parse(responseData);
      } catch {
        parsedResponse = responseData;
      }

      setResponse({
        status: result.status,
        statusText: result.statusText,
        headers: Object.fromEntries(result.headers.entries()),
        data: parsedResponse,
        responseTime,
        timestamp: new Date().toISOString()
      });

    } catch (err) {
      setResponse({
        status: 0,
        statusText: 'Error',
        headers: {},
        data: { error: err.message },
        responseTime: 0,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'error';
    return 'default';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          {t('api_playground')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('interactive_api_testing')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* API Endpoints */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('available_endpoints')}
              </Typography>
              
              {endpoints.map((endpoint) => (
                <Box
                  key={`${endpoint.method}-${endpoint.path}`}
                  sx={{
                    p: 2,
                    mb: 1,
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 1,
                    cursor: 'pointer',
                    backgroundColor: selectedEndpoint === endpoint.path ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    }
                  }}
                  onClick={() => handleEndpointSelect(endpoint)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip
                      label={endpoint.method}
                      size="small"
                      color={endpoint.method === 'GET' ? 'primary' : 'secondary'}
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {endpoint.path}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {endpoint.description}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Request Builder */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('request_builder')}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>{t('method')}</InputLabel>
                  <Select
                    value={method}
                    label={t('method')}
                    onChange={(e) => setMethod(e.target.value)}
                  >
                    <MenuItem value="GET">{t('get')}</MenuItem>
                    <MenuItem value="POST">{t('post')}</MenuItem>
                    <MenuItem value="PUT">{t('put')}</MenuItem>
                    <MenuItem value="DELETE">{t('http_delete')}</MenuItem>
                  </Select>
                </FormControl>
                
                <TextField
                  fullWidth
                  size="small"
                  label={t('url')}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="/api/v1/metrics/system"
                />
                
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={executeRequest}
                  disabled={loading || !url}
                  sx={{ minWidth: 120 }}
                >
                  {loading ? t('sending') : t('send')}
                </Button>
              </Box>

              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
                <Tab label={t('api_headers')} />
                <Tab label={t('api_body')} />
              </Tabs>

              {tabValue === 0 && (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('headers_json')}
                  value={headers}
                  onChange={(e) => setHeaders(e.target.value)}
                  placeholder={t('headers_placeholder')}
                  sx={{ fontFamily: 'monospace' }}
                />
              )}

              {tabValue === 1 && (
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label={t('request_body_json')}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={t('json_placeholder')}
                  disabled={method === 'GET'}
                  sx={{ fontFamily: 'monospace' }}
                />
              )}
            </CardContent>
          </Card>

          {/* Response */}
          {response && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">{t('response')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                      label={`${response.status} ${response.statusText}`}
                      color={getStatusColor(response.status)}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {response.responseTime}ms
                    </Typography>
                  </Box>
                </Box>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{t('response_headers')}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper sx={{ p: 2, backgroundColor: '#1e1e1e' }}>
                      <ReactJsonView
                        src={response.headers}
                        theme="monokai"
                        displayDataTypes={false}
                        displayObjectSize={false}
                        enableClipboard={false}
                        style={{ backgroundColor: 'transparent' }}
                      />
                    </Paper>
                  </AccordionDetails>
                </Accordion>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>{t('response_body')}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper sx={{ p: 2, backgroundColor: '#1e1e1e' }}>
                      <ReactJsonView
                        src={response.data}
                        theme="monokai"
                        displayDataTypes={false}
                        displayObjectSize={false}
                        enableClipboard={true}
                        style={{ backgroundColor: 'transparent' }}
                      />
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* API Documentation */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                API Documentation
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<DescriptionIcon />}
                  href="/api/v1/docs"
                  target="_blank"
                >
                  {t('openapi_docs_swagger')}
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<CodeIcon />}
                  href="/api/v1/redoc"
                  target="_blank"
                >
                  {t('redoc_documentation')}
                </Button>
              </Box>

              <Typography variant="body1" color="text.secondary">
                {t('api_comprehensive_description')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default APIPlayground;
